import * as React from "react";

import { datadogRum } from "@datadog/browser-rum";

import { ScanLoadErrorPanel } from "features/dynamic-web-twain/scan-load-status.component";
import { isChunkLoadError } from "utils/lazy-with-retry.util";

interface ScanLoadErrorBoundaryState {
  readonly error?: Error;
}

/**
 * DEV-2806. Keeps a failed scan chunk inside the dialog. Previously the rejection
 * reached RouteErrorBoundary and replaced the whole patient page — customers
 * reported it as "scanning breaks the app", and the recovery it offers is a
 * reload the Ambient Scribe's beforeunload guard vetoes anyway.
 *
 * Deliberately narrow: only chunk-load failures are handled here. Anything else is
 * a real bug in the scan UI and still belongs at the route boundary, where it gets
 * reported rather than dressed up as a network hiccup.
 */
export class ScanLoadErrorBoundary extends React.Component<
  { readonly children: React.ReactNode },
  ScanLoadErrorBoundaryState
> {
  state: ScanLoadErrorBoundaryState = {};

  static getDerivedStateFromError(error: Error): ScanLoadErrorBoundaryState {
    if (!isChunkLoadError(error)) throw error;
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("Scan Document chunk failed to load", error);
    // Keeping the failure inside the dialog also took it out of RouteErrorBoundary,
    // which reports to Datadog — and plain React.lazy drops `lazyWithRetry`'s
    // chunk_reload_* actions. Without this a fleet-wide stale-asset regression on
    // this dialog produces no telemetry at all and we learn about it from Intercom.
    datadogRum.addError(error, { feature: "scan-document-chunk-load" });
  }

  render() {
    if (this.state.error) {
      return <ScanLoadErrorPanel />;
    }
    return this.props.children;
  }
}
